import React, { useEffect, useState, useCallback } from "react";
import { Mail, RefreshCw, Send, RotateCcw, Eye, Save, AlertCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export const AdminEmailTemplatesPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [templates, setTemplates] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [draft, setDraft] = useState({ subject: "", body_html: "" });
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [recentSends, setRecentSends] = useState([]);

    const load = useCallback(async () => {
        try {
            const [tplRes, logRes] = await Promise.all([
                api.get("/admin/email-templates", { headers }),
                api.get("/admin/automation/log?limit=20", { headers }),
            ]);
            setTemplates(tplRes.data.templates);
            setRecentSends(logRes.data.entries);
            if (!selectedKey && tplRes.data.templates.length > 0) {
                setSelectedKey(tplRes.data.templates[0].key);
                setDraft({
                    subject: tplRes.data.templates[0].subject,
                    body_html: tplRes.data.templates[0].body_html,
                });
            }
        } catch {
            toast.error("Could not load templates");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const selected = templates.find((t) => t.key === selectedKey);

    const selectTemplate = (key) => {
        const t = templates.find((x) => x.key === key);
        if (!t) return;
        setSelectedKey(key);
        setDraft({ subject: t.subject, body_html: t.body_html });
        setPreview(null);
    };

    const save = async () => {
        if (!draft.subject.trim() || !draft.body_html.trim()) return toast.error("Subject and body are required");
        setBusy(true);
        try {
            await api.put(`/admin/email-templates/${selectedKey}`, draft, { headers });
            toast.success("Template saved");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
        setBusy(false);
    };

    const resetToDefault = async () => {
        if (!window.confirm("Restore the original copy for this template? Your custom edits will be lost.")) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/email-templates/${selectedKey}/reset`, {}, { headers });
            toast.success("Restored to default");
            setDraft({ subject: data.template.subject, body_html: data.template.body_html });
            await load();
        } catch {
            toast.error("Reset failed");
        }
        setBusy(false);
    };

    const showPreview = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/email-templates/${selectedKey}/preview`, {}, { headers });
            setPreview(data);
        } catch {
            toast.error("Preview failed");
        }
        setBusy(false);
    };

    const testSend = async () => {
        if (!testEmail.trim()) return toast.error("Enter a test email");
        setBusy(true);
        try {
            await api.post(`/admin/email-templates/${selectedKey}/test-send`, { email: testEmail.trim() }, { headers });
            toast.success(`Test sent to ${testEmail}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test send failed");
        }
        setBusy(false);
    };

    const runSweeps = async () => {
        if (!window.confirm("Run all automation sweeps right now (abandoned cart, birthday, review request)? This will send real emails to customers who match.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/automation/run-sweeps", {}, { headers });
            const total = Object.values(data).filter((v) => typeof v === "object" && v.sent !== undefined)
                .reduce((a, v) => a + (v.sent || 0), 0);
            toast.success(`Sweeps complete · ${total} emails sent`);
            await load();
        } catch {
            toast.error("Sweep failed");
        }
        setBusy(false);
    };

    return (
        <div className="space-y-5" data-testid="admin-email-templates-panel">
            <div className="glass-elevated rounded-2xl p-5 flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Automation</p>
                    <h2 className="font-display text-2xl font-extrabold text-white italic tracking-tighter">Email triggers & templates</h2>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                        Edit the copy customers receive automatically. Sweeps run hourly in the background — or trigger them manually.
                    </p>
                </div>
                <button onClick={runSweeps} disabled={busy} className="btn-solid disabled:opacity-50" data-testid="run-sweeps-now">
                    <Zap size={14} /> Run sweeps now
                </button>
            </div>

            <div className="grid lg:grid-cols-[260px_1fr] gap-4">
                {/* Template list */}
                <div className="space-y-2">
                    {templates.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => selectTemplate(t.key)}
                            className={`w-full text-left rounded-xl p-3 border transition-colors ${selectedKey === t.key ? "border-[var(--gold)]/50 bg-[rgba(233,188,11,0.06)]" : "border-white/10 hover:border-white/30 bg-black/30"}`}
                            data-testid={`template-${t.key}`}
                        >
                            <p className={`text-sm font-bold ${selectedKey === t.key ? "text-[var(--gold)]" : "text-white"}`}>{t.label}</p>
                            <p className="text-[10px] text-[var(--text-muted)] font-mono mt-1">
                                {t.is_default ? "Default copy" : "Custom"}
                            </p>
                        </button>
                    ))}
                </div>

                {/* Editor */}
                {selected && (
                    <div className="space-y-4">
                        <div className="glass-elevated rounded-2xl p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Editing</p>
                                    <h3 className="font-display text-xl font-extrabold text-white italic tracking-tighter">{selected.label}</h3>
                                </div>
                                {!selected.is_default && (
                                    <button onClick={resetToDefault} disabled={busy} className="px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-white/10 text-[var(--text-secondary)] hover:text-white hover:border-white/30 inline-flex items-center gap-1 disabled:opacity-40" data-testid="template-reset">
                                        <RotateCcw size={11} /> Reset to default
                                    </button>
                                )}
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Subject</p>
                                    <input
                                        value={draft.subject}
                                        onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                                        className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="template-subject"
                                    />
                                </div>
                                <div>
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Body HTML</p>
                                    <textarea
                                        value={draft.body_html}
                                        onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
                                        rows={14}
                                        className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-xs text-white font-mono focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="template-body"
                                    />
                                    <p className="text-[10px] text-[var(--text-muted)] mt-1">
                                        Placeholders: {selected.placeholders.map((p) => `{${p}}`).join(", ") || "none"}.
                                        The branded header/footer is added automatically.
                                    </p>
                                </div>

                                <div className="flex flex-wrap items-center gap-2 pt-2">
                                    <button onClick={save} disabled={busy} className="btn-solid disabled:opacity-50" data-testid="template-save">
                                        <Save size={14} /> Save
                                    </button>
                                    <button onClick={showPreview} disabled={busy} className="px-3 py-2 rounded-full text-xs font-mono uppercase tracking-widest border border-white/10 text-[var(--text-secondary)] hover:text-white hover:border-white/30 inline-flex items-center gap-1 disabled:opacity-40" data-testid="template-preview">
                                        <Eye size={12} /> Preview
                                    </button>
                                    <div className="ml-auto flex items-center gap-2">
                                        <input
                                            value={testEmail}
                                            onChange={(e) => setTestEmail(e.target.value)}
                                            placeholder="you@example.com"
                                            className="w-44 px-3 py-2 bg-black/40 border border-white/10 rounded-full text-xs text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                            data-testid="template-test-email"
                                        />
                                        <button onClick={testSend} disabled={busy || !testEmail.trim()} className="px-3 py-2 rounded-full text-xs font-mono uppercase tracking-widest border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[rgba(233,188,11,0.08)] disabled:opacity-40 inline-flex items-center gap-1" data-testid="template-test-send">
                                            <Send size={12} /> Send test
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Preview pane */}
                        {preview && (
                            <div className="glass-elevated rounded-2xl p-5" data-testid="template-preview-pane">
                                <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Preview</p>
                                <p className="text-sm text-white mb-3 font-bold">Subject: <span className="font-normal text-[var(--text-secondary)]">{preview.subject}</span></p>
                                <iframe
                                    title="email-preview"
                                    srcDoc={preview.body_html}
                                    className="w-full h-[500px] rounded-xl bg-white border-0"
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Recent automated sends */}
            <div className="glass-elevated rounded-2xl p-5" data-testid="automation-log">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Activity</p>
                        <h3 className="font-display text-lg font-bold text-white">Recent automated sends</h3>
                    </div>
                    <button onClick={load} className="px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-white/10 text-[var(--text-secondary)] hover:text-white hover:border-white/30 inline-flex items-center gap-1">
                        <RefreshCw size={11} /> Refresh
                    </button>
                </div>
                {recentSends.length === 0 && (
                    <p className="text-sm text-[var(--text-secondary)] inline-flex items-center gap-2"><AlertCircle size={14} /> No automated sends yet.</p>
                )}
                <div className="space-y-1">
                    {recentSends.map((e, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-black/30 border border-white/5 text-xs">
                            <div className="min-w-0 flex-1">
                                <p className="text-white truncate">{e.subject}</p>
                                <p className="text-[10px] text-[var(--text-muted)] font-mono">{e.email} · {new Date(e.sent_at).toLocaleString("en-AU")}</p>
                            </div>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] px-2 py-1 rounded-full bg-[rgba(233,188,11,0.08)] border border-[var(--gold)]/30">
                                {e.trigger_key}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
